<script setup lang="ts">
import { computed, ref } from 'vue';
import { Download } from 'lucide-vue-next';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  TRACK_LIST_COLUMNS,
  DEFAULT_TRACK_LIST_COLUMN_IDS,
  useTrackListColumns,
} from '@/composables/trackListColumns';
import { storeSearchExportColumns } from '@/composables/searchExportColumns';
import { startListExport } from '@/composables/startListExport';
import { useInspectorStore } from '@/stores/inspectorStore';
import type { TrackListExportSource } from '@/types/trackListExport';

/**
 * The export column list. The list's Export button opens it in the inspector.
 * The person ticks the columns the spreadsheet should hold. Pressing Export
 * here stores that choice for next time, then starts the export.
 *
 * Ticking edits a draft. The stored choice changes only when Export is
 * pressed, so closing the list without exporting leaves it as it was.
 *
 * Tick order is the export's column order. Ticking a column adds it at the
 * end, so the person builds the sheet from left to right. The number beside a
 * ticked column is its place in the sheet.
 *
 * Deliberately not `ColumnSettingsInspectorContent`. That panel edits a live
 * table: sizes, pins, sort and saved views. This list only picks columns.
 */
const props = withDefaults(
  defineProps<{
    source: TrackListExportSource;
    /** The list's name for the toast and the email, such as "Account search". */
    label: string;
    /** The columns the list starts with: the person's last export choice. */
    initialColumnIds: string[];
    /** The columns to offer. A track list offers its fixed set. */
    columnOptions?: ReadonlyArray<{ id: string; label: string }>;
    /** What Reset puts back. */
    resetColumnIds?: string[];
  }>(),
  {
    columnOptions: () => TRACK_LIST_COLUMNS,
    resetColumnIds: () => [...DEFAULT_TRACK_LIST_COLUMN_IDS],
  },
);

const inspectorStore = useInspectorStore();
const trackListColumnIds = useTrackListColumns();

const offeredIds = computed(() => new Set(props.columnOptions.map((column) => column.id)));

/** Keep only columns this list offers, so a stale choice cannot send a ghost. */
function offeredOnly(columnIds: string[]): string[] {
  return columnIds.filter((columnId) => offeredIds.value.has(columnId));
}

const draftColumnIds = ref<string[]>(offeredOnly(props.initialColumnIds));
const draftSet = computed(() => new Set(draftColumnIds.value));
const hasNoColumns = computed(() => draftColumnIds.value.length === 0);

// --- Search ------------------------------------------------------------------

const searchText = ref('');
const normalizedSearch = computed(() => searchText.value.trim().toLowerCase());

const shownColumns = computed(() =>
  normalizedSearch.value === ''
    ? props.columnOptions
    : props.columnOptions.filter((column) => column.label.toLowerCase().includes(normalizedSearch.value)),
);

/** Escape clears the search first. Only an empty search lets the key reach the host. */
function onSearchEscape(event: KeyboardEvent) {
  if (searchText.value === '') {
    return;
  }
  event.stopPropagation();
  searchText.value = '';
}

// --- Ticking -----------------------------------------------------------------

function isSelected(columnId: string): boolean {
  return draftSet.value.has(columnId);
}

function toggle(columnId: string, checked: boolean): void {
  if (checked) {
    if (!draftSet.value.has(columnId)) {
      draftColumnIds.value = [...draftColumnIds.value, columnId];
    }
    return;
  }
  draftColumnIds.value = draftColumnIds.value.filter((id) => id !== columnId);
}

function selectAll(): void {
  const missingIds = props.columnOptions
    .map((column) => column.id)
    .filter((columnId) => !draftSet.value.has(columnId));
  draftColumnIds.value = [...draftColumnIds.value, ...missingIds];
}

function resetColumns(): void {
  draftColumnIds.value = offeredOnly(props.resetColumnIds);
}

/** Place in the export, counted from 1. */
function exportPosition(columnId: string): number {
  return draftColumnIds.value.indexOf(columnId) + 1;
}

// --- Export ------------------------------------------------------------------

const isSubmitting = ref(false);

/** Store the choice where the next Export on this kind of list will read it. */
function storeChoice(columnIds: string[]): void {
  if (props.source.type === 'search') {
    storeSearchExportColumns(props.source.entity, columnIds);
    return;
  }
  trackListColumnIds.value = [...columnIds];
}

/**
 * Close the list. It was pushed on top of the list it came from, so this goes
 * back to that list. If it is the only view, there is nothing to go back to,
 * so the inspector closes. `keepForwardHistory: false` because this is a
 * dismissal: the header's forward arrow must not point back at this list.
 */
function closeList(): void {
  if (inspectorStore.canGoBack) {
    inspectorStore.pop({ keepForwardHistory: false });
    return;
  }
  inspectorStore.close();
}

async function saveAndExport(): Promise<void> {
  if (hasNoColumns.value || isSubmitting.value) {
    return;
  }
  const columnIds = [...draftColumnIds.value];
  storeChoice(columnIds);
  isSubmitting.value = true;
  try {
    const started = await startListExport({ source: props.source, columns: columnIds, label: props.label });
    // A failed start keeps the list open, so the person can try again.
    if (started) {
      closeList();
    }
  } finally {
    isSubmitting.value = false;
  }
}

const isTrackList = computed(() => props.source.type !== 'search');
</script>

<template>
  <div class="flex flex-col gap-3" data-testid="track-list-column-settings">
    <p class="text-muted-foreground text-xs">
      These columns go into the spreadsheet, in the order you tick them.
      <template v-if="isTrackList">The cards on screen are set by the density toggle instead.</template>
    </p>

    <label
      class="flex h-8 items-center gap-2 rounded-lg bg-foreground/[0.06] px-2.5 focus-within:ring-1 focus-within:ring-foreground/20"
    >
      <MagnifyingGlassIcon class="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <input
        v-model="searchText"
        type="search"
        placeholder="Search columns"
        aria-label="Search columns"
        data-testid="track-list-columns-search"
        class="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
        @keydown.esc="onSearchEscape"
      />
      <button
        v-if="searchText"
        type="button"
        class="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
        aria-label="Clear search"
        @click="searchText = ''"
      >
        <XMarkIcon class="size-4" aria-hidden="true" />
      </button>
    </label>

    <div class="flex gap-2">
      <Button variant="outline" size="sm" data-testid="track-list-columns-select-all" @click="selectAll">
        Select all
      </Button>
      <Button variant="outline" size="sm" data-testid="track-list-columns-reset" @click="resetColumns">
        Reset
      </Button>
    </div>

    <ul class="flex flex-col gap-1">
      <li
        v-for="column in shownColumns"
        :key="column.id"
        class="hover:bg-muted/50 flex items-center gap-2 rounded px-1.5 py-1"
      >
        <Checkbox
          :id="`track-list-column-${column.id}`"
          :model-value="isSelected(column.id)"
          :data-testid="`track-list-column-${column.id}`"
          @update:model-value="(checked: boolean | 'indeterminate') => toggle(column.id, checked === true)"
        />
        <label :for="`track-list-column-${column.id}`" class="flex-1 cursor-pointer text-sm">
          {{ column.label }}
        </label>
        <span v-if="isSelected(column.id)" class="text-muted-foreground text-xs tabular-nums">
          {{ exportPosition(column.id) }}
        </span>
      </li>
      <li v-if="shownColumns.length === 0" class="text-muted-foreground px-1.5 py-1 text-sm">
        No column matches “{{ searchText.trim() }}”.
      </li>
    </ul>

    <!--
      The Export button stays in view at the bottom of the scroll area, so a
      long list never hides it. On a phone the list is in the bottom sheet,
      which covers the area bar, so the button sits above the bar.

      The scroll area has bottom padding, so a sticky footer stops that far
      above its edge. The shadow paints the panel colour over that gap, so no
      column shows through below the button.
    -->
    <div
      class="sticky bottom-0 flex flex-col items-end gap-1 border-t border-border/60 bg-popover py-2 shadow-[0_2.5rem_0_0_var(--popover)]"
    >
      <p
        v-if="hasNoColumns"
        id="track-list-columns-none-hint"
        class="text-destructive self-stretch text-xs"
        data-testid="track-list-columns-none-hint"
      >
        Pick at least one column. An export needs something to put in the sheet.
      </p>
      <Button
        size="sm"
        :disabled="hasNoColumns || isSubmitting"
        :aria-describedby="hasNoColumns ? 'track-list-columns-none-hint' : undefined"
        data-testid="track-list-columns-export"
        @click="saveAndExport"
      >
        <Download class="size-3.5 shrink-0" aria-hidden="true" />
        <span>{{ isSubmitting ? 'Starting…' : 'Export' }}</span>
      </Button>
    </div>
  </div>
</template>
